import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import config from '@config'
import usePageMetadata from '@hooks/usePageMetadata'
import ImageObjectSchema from '@components/Schema/ImageObjectSchema'
import { getPreparedImages } from '@components/Schema/images'
import PersonSchema from '@components/Schema/PersonSchema'
import { ArticleSchemaData } from '@components/Schema/types'
import {
  getAuthorsId,
  getImagesId,
  getPreparedAuthors,
  getVideosId,
} from '@components/Schema/utils'
import SchemaHTMLElement from '@components/SchemaHTMLElement'

const {
  backend: { baseUrl },
  schema: { organizationId, articleSchemaUrl },
} = config

const ArticleSchema: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const unsubscribeFnRef = useRef<() => void>(undefined)
  const queryCache = queryClient.getQueryCache()
  const [description, setDescription] = useState<string>('')
  const pageMetadata = usePageMetadata()

  const updateArticleSchema = useCallback(
    (args: any) => {
      if (
        args.query.queryKey[0] === 'articleSchemaData' &&
        args.type === 'updated'
      ) {
        const data = queryClient.getQueryData<ArticleSchemaData>([
          'articleSchemaData',
        ])

        if (data?.description) {
          setDescription(data.description)
        }
      }
    },
    [queryClient],
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateArticleSchema)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, updateArticleSchema])

  const {
    htmlTitle,
    title,
    url,
    authors,
    lastModifiedDate,
    publishedDate,
    schema,
    isPlus,
    id: articleId,
    adChannel,
  } = pageMetadata
  const dateModified = lastModifiedDate
    ? new Date(lastModifiedDate).toISOString()
    : ''
  const datePublished = publishedDate
    ? new Date(publishedDate).toISOString()
    : ''

  const preparedAuthors = useMemo(() => getPreparedAuthors(authors), [authors])

  const authorsId = useMemo(
    () => getAuthorsId(preparedAuthors),
    [preparedAuthors],
  )

  const imageSet = useMemo(
    () => getPreparedImages(schema?.image),
    [schema?.image],
  )

  const imageIds = useMemo(() => getImagesId(imageSet), [imageSet])
  const { video, videos } = schema || {}
  const teaserVideoIds = useMemo(
    () => getVideosId(videos?.length ? videos : video ? [video] : []),
    [video, videos],
  )

  return (
    <>
      {!!preparedAuthors.length &&
        preparedAuthors?.map((author) => (
          <PersonSchema key={author.name} {...author} />
        ))}
      {!!imageSet.length &&
        imageSet.map((image) => (
          <ImageObjectSchema key={image.url} {...image} />
        ))}
      <SchemaHTMLElement
        scriptKey="article"
        schemaObject={{
          '@type': 'NewsArticle',
          '@id': `${articleSchemaUrl}${articleId}`,
          headline: schema?.orgTitle || htmlTitle || title,
          description,
          isPartOf: {
            '@id': `${baseUrl}${url}`,
            // To ensure proper integration with Google News Showcase,
            // we explicitly include @type, name, and productID within the isPartOf field.
            // Without these attributes, GNS may fail to recognize the publisher,
            // which can prevent the second intervention from displaying as expected.
            '@type': ['CreativeWork', 'Product'],
            name: 'Blick',
            productID: 'blick.ch:showcase',
          },
          mainEntityOfPage: {
            '@id': `${baseUrl}${url}`,
          },
          dateModified,
          datePublished,
          articleSection: adChannel,
          ...(authorsId.length ? { author: authorsId } : {}),
          publisher: {
            '@id': organizationId,
            name: 'Blick',
          },
          ...(imageIds.length ? { image: imageIds } : {}),
          isAccessibleForFree: isPlus ? 'False' : 'True',
          ...(teaserVideoIds ? { video: teaserVideoIds } : {}),
          ...(isPlus
            ? {
                hasPart: {
                  '@type': 'WebPageElement',
                  isAccessibleForFree: 'False',
                  cssSelector: '.paywallContent',
                },
              }
            : {}),
        }}
      />
    </>
  )
}

export default ArticleSchema
